import * as PIXI from 'pixi.js';
import { globalEventBus, GameEvents } from '../core/EventBus.js';

/**
 * 拖拽控制器
 * 处理拼图块的拖拽和旋转交互
 */
export class DragController {
    constructor(app, puzzleBoard, config) {
        this.app = app;
        this.puzzleBoard = puzzleBoard;
        this.config = config;

        this.events = globalEventBus;

        // 拖拽状态
        this.isDragging = false;
        this.dragTarget = null;
        this.dragOffset = { x: 0, y: 0 };
        this.lastDragPosition = { x: 0, y: 0 };

        // 是否启用
        this.enabled = true;

        // 旋转配置
        this.rotationEnabled = config.rotation?.enabled ?? true;

        // 动画管理
        this.activeAnimations = new Set();

        this.init();
    }

    /**
     * 初始化事件监听
     */
    init() {
        // 监听舞台事件
        this.app.stage.eventMode = 'static';
        this.app.stage.hitArea = this.app.screen;

        // 鼠标/触摸移动
        this.app.stage.on('pointermove', this.onPointerMove.bind(this));

        // 鼠标/触摸释放
        this.app.stage.on('pointerup', this.onPointerUp.bind(this));
        this.app.stage.on('pointerupoutside', this.onPointerUp.bind(this));

        // 鼠标滚轮旋转
        this.app.canvas.addEventListener('wheel', (e) => {
            e.preventDefault();
            this.onWheel(e);
        }, { passive: false });

        // 键盘旋转
        document.addEventListener('keydown', this.onKeyDown.bind(this));
    }

    /**
     * 为拼图块绑定拖拽事件
     * @param {Piece} piece 拼图块
     */
    bindPiece(piece) {
        const container = piece.container;

        container.eventMode = 'static';
        container.cursor = 'grab';

        container.on('pointerdown', (e) => this.onPointerDown(e, piece));
    }

    /**
     * 为所有拼图块绑定事件
     */
    bindAllPieces() {
        for (const piece of this.puzzleBoard.pieces) {
            // 修改：即使是已吸附的拼图也允许绑定事件
            this.bindPiece(piece);
        }
    }

    /**
     * 鼠标/触摸按下
     * @param {PIXI.FederatedPointerEvent} event 事件对象
     * @param {Piece} piece 拼图块
     */
    onPointerDown(event, piece) {
        if (!this.enabled) return;

        // 左键拖拽
        if (event.button === 0) {
            this.startDrag(event, piece);
        }
    }

    /**
     * 开始拖拽
     * @param {PIXI.FederatedPointerEvent} event 事件对象
     * @param {Piece} piece 拼图块
     */
    startDrag(event, piece) {
        // 关键修复：在开始拖拽并改变容器之前，必须强制停止该碎片及其组上所有正在进行的动画
        // 否则如果旋转动画在容器切换期间继续运行，会产生坐标系错位导致的瞬移
        const group = this.puzzleBoard.getGroup(piece);
        this.activeAnimations.forEach(anim => {
            // 检查动画是否涉及当前组中的任何碎片
            const isRelated = anim.pieces && Array.from(anim.pieces).some(p => group.has(p));
            const isSingleRelated = anim.piece && group.has(anim.piece);
            
            if (isRelated || isSingleRelated) {
                anim.finish();
            }
        });

        this.isDragging = true;
        this.dragTarget = piece;

        // 获取整个组
        // const group = this.puzzleBoard.getGroup(piece); // 移到上方用于动画检查

        // 如果开始拖拽已吸附的拼图，解除吸附状态
        for (const p of group) {
            if (p.isSnapped) {
                p.releaseSnap();
            }
            // 触发悬浮动效 (仅阴影)
            p.lift();
        }

        // 确保所有 pivot 在中心
        for (const p of group) {
            this.ensurePivotCentered(p);
        }

        // === 创建临时拖拽容器 ===
        this.dragGroupContainer = new PIXI.Container();
        this.app.stage.addChild(this.dragGroupContainer); // 添加到舞台最上层

        // 1. 计算组的几何中心
        let minX = Infinity, maxX = -Infinity;
        let minY = Infinity, maxY = -Infinity;

        for (const p of group) {
            // 获取在父容器(puzzleBoard.container)中的边界
            // 注意：这里 p.container.x/y 是相对于 puzzleBoard.container 的
            const x = p.container.x;
            const y = p.container.y;
            
            // 简单取中心点 (假设 x/y 是中心)
            // 更严谨应该用 bounds，但这里我们只需要相对关系
            if (x < minX) minX = x;
            if (x > maxX) maxX = x;
            if (y < minY) minY = y;
            if (y > maxY) maxY = y;
        }

        const centerX = (minX + maxX) / 2;
        const centerY = (minY + maxY) / 2;

        // 2. 将临时容器放置在组中心 (转换到全局坐标)
        // 注意：我们直接把 dragGroupContainer 放在 stage 上，所以使用全局坐标
        // puzzleBoard.container 可能有自己的变换，虽然通常它只是 (0,0)
        // 为了保险，使用 toGlobal
        const globalCenter = this.puzzleBoard.container.toGlobal({ x: centerX, y: centerY });
        this.dragGroupContainer.position.set(globalCenter.x, globalCenter.y);

        // 3. 将碎片移入临时容器
        for (const p of group) {
            // 计算相对于组中心的局部坐标
            // 原位置 (相对于 puzzleBoard) - 组中心 (相对于 puzzleBoard)
            const localX = p.container.x - centerX;
            const localY = p.container.y - centerY;

            // 存入碎片，用于恢复
            p.originalParent = p.container.parent;
            
            // 转移父节点
            this.dragGroupContainer.addChild(p.container);
            
            // 设置新的局部坐标
            p.container.position.set(localX, localY);
        }

        // 4. 整体放大
        // 使用简单的缓动
        this.animateContainerScale(this.dragGroupContainer, 1.1);

        // 5. 设置拖拽偏移
        // 基于 dragGroupContainer 的位置
        const position = event.global;
        this.dragOffset.x = position.x - this.dragGroupContainer.x;
        this.dragOffset.y = position.y - this.dragGroupContainer.y;

        this.events.emit(GameEvents.PIECE_PICKUP, piece);
    }

    /**
     * 容器缩放动画
     */
    animateContainerScale(container, targetScale) {
        const startScale = container.scale.x;
        const startTime = Date.now();
        const duration = 200;

        const animate = () => {
            // 如果容器已被销毁（例如快速释放），停止动画
            if (container.destroyed) return;

            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3); // Ease out cubic

            const current = startScale + (targetScale - startScale) * eased;
            container.scale.set(current);

            if (progress < 1) {
                requestAnimationFrame(animate);
            }
        };
        requestAnimationFrame(animate);
    }

    /**
     * 鼠标/触摸移动
     * @param {PIXI.FederatedPointerEvent} event 事件对象
     */
    onPointerMove(event) {
        if (!this.isDragging || !this.dragGroupContainer) return;

        const position = event.global;
        const newX = position.x - this.dragOffset.x;
        const newY = position.y - this.dragOffset.y;

        this.dragGroupContainer.position.set(newX, newY);
    }

    /**
     * 鼠标/触摸释放
     * @param {PIXI.FederatedPointerEvent} event 事件对象
     */
    onPointerUp(event) {
        if (!this.isDragging || !this.dragGroupContainer) return;

        // 关键修复：如果在拖拽过程中正在进行旋转动画（尚未结束）
        // 必须强制立即完成动画，将碎片的坐标和角度设置到最终的目标状态（局部坐标）
        // 否则，当我们将碎片移回世界坐标系后，未停止的动画循环会继续使用旧的局部坐标更新位置，导致碎片瞬移到左上角
        this.activeAnimations.forEach(anim => anim.finish());
        this.activeAnimations.clear();

        const piece = this.dragTarget;
        const group = this.puzzleBoard.getGroup(piece);

        // 关键修复：在计算还原坐标前，必须先把容器缩放重置回 1.0
        // 否则 getGlobalPosition 获取的是被视觉放大后的位置，会导致碎片间隙永久变大
        this.dragGroupContainer.scale.set(1.0);

        // 1. 还原坐标系
        // 将所有碎片移回 puzzleBoard.container
        for (const p of group) {
            // 获取当前的全局坐标
            const globalPos = p.container.getGlobalPosition();
            
            // 转换回 puzzleBoard 的局部坐标
            // 如果 originalParent 存在则使用它，否则默认 puzzleBoard.container
            const parent = p.originalParent || this.puzzleBoard.container;
            const localPos = parent.toLocal(globalPos);
            
            parent.addChild(p.container);
            p.container.position.copyFrom(localPos);
            p.drop(); // 恢复阴影
            p.container.cursor = 'grab';
        }

        // 2. 销毁临时容器
        this.dragGroupContainer.destroy();
        this.dragGroupContainer = null;

        // 3. 检查吸附
        // 注意：现在碎片已经回到了 puzzleBoard，坐标已更新，可以安全检查
        this.puzzleBoard.checkSnap(piece);

        this.events.emit(GameEvents.PIECE_DROP, piece);

        // 重置状态
        this.isDragging = false;
        this.dragTarget = null;
    }

    /**
     * 鼠标滚轮事件（旋转）
     * @param {WheelEvent} event 滚轮事件
     */
    onWheel(event) {
        if (!this.enabled || !this.rotationEnabled) return;

        // 获取鼠标位置
        const rect = this.app.canvas.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;

        // 查找鼠标下的拼图块
        const piece = this.findPieceAtPosition(x, y);

        if (piece) {
            // 向下滚动 (deltaY > 0) -> 顺时针 (+90)
            // 向上滚动 (deltaY < 0) -> 逆时针 (-90)
            const direction = event.deltaY > 0 ? 90 : -90;
            
            // 获取整个组
            const group = this.puzzleBoard.getGroup(piece);
            console.log(`Rotating group. Piece: ${piece.col},${piece.row}. Group size: ${group.size}`);
            
            this.rotateGroup(group, direction);
        }
    }

    /**
     * 确保拼图块的 Pivot 在中心
     * @param {Piece} piece 拼图块
     */
    ensurePivotCentered(piece) {
        if (piece.container.pivot.x === 0 && piece.container.pivot.y === 0) {
            const svgSize = piece.size + piece.padding * 2;
            const centerOffset = svgSize / 2;

            piece.container.pivot.set(centerOffset, centerOffset);

            // 调整坐标以保持视觉位置不变
            piece.container.x += centerOffset;
            piece.container.y += centerOffset;
        }
    }

    /**
     * 旋转一组拼图
     * @param {Set<Piece>} group 拼图组
     * @param {number} degrees 旋转角度
     */
    rotateGroup(group, degrees) {
        const pieces = Array.from(group);
        console.log(`rotateGroup called. Group size: ${pieces.length}`);

        // 检查是否可以旋转（没有正在旋转的块）
        if (pieces.some(p => p.isRotating)) {
            console.log("Rotation blocked: pieces are rotating.");
            return;
        }

        // 修改：旋转时解除吸附状态
        pieces.forEach(p => {
            if (p.isSnapped) {
                p.releaseSnap();
            }
        });

        // 1. 确保所有块的 pivot 都在中心
        // 这一步至关重要，必须在计算边界前完成
        pieces.forEach(p => this.ensurePivotCentered(p));

        // 2. 计算组的边界框中心（旋转中心）
        let minX = Infinity, maxX = -Infinity;
        let minY = Infinity, maxY = -Infinity;

        pieces.forEach(p => {
            const halfSize = p.size / 2;
            // 此时 container.x/y 已经是几何中心
            minX = Math.min(minX, p.container.x - halfSize);
            maxX = Math.max(maxX, p.container.x + halfSize);
            minY = Math.min(minY, p.container.y - halfSize);
            maxY = Math.max(maxY, p.container.y + halfSize);
        });

        const gCx = (minX + maxX) / 2;
        const gCy = (minY + maxY) / 2;
        
        console.log(`Group Center: ${gCx}, ${gCy}`);

        // 3. 计算每个块的目标状态
        const animations = pieces.map(p => {
            // ... (keep existing logic)
            const startRot = p.rotation;
            let targetRot = (startRot + degrees) % 360;
            if (targetRot < 0) targetRot += 360;

            // 记录相对于组中心的初始偏移量 (dx, dy)
            const currX = p.container.x;
            const currY = p.container.y;
            const relX = currX - gCx;
            const relY = currY - gCy;

            // 计算最终目标位置（仅用于最后校准）
            let ndx, ndy;
            if (degrees === 90) {
                ndx = -relY;
                ndy = relX;
            } else { 
                ndx = relY;
                ndy = -relX;
            }
            const targetX = gCx + ndx;
            const targetY = gCy + ndy;

            p.isRotating = true;
            p.rotation = targetRot; 

            return {
                piece: p,
                startRot: startRot,
                targetRot: targetRot,
                deltaRot: (degrees === 90) ? 90 : -90, 
                // 关键：保存初始相对偏移，用于每帧计算旋转后的位置
                relX: relX,
                relY: relY,
                targetX: targetX,
                targetY: targetY
            };
        });
        
        console.log(`Starting rotation animation for ${pieces.length} pieces.`);

        // 4. 执行动画
        const duration = 200;
        const startTime = Date.now();
        let animationFrameId = null;

        // 定义动画控制对象
        const animControl = {
            id: Math.random(),
            isCancelled: false, // 标志位：防止僵尸帧
            pieces: group,      // 记录关联的碎片组，用于 startDrag 时的清理判定
            finish: () => {
                if (animControl.isCancelled) return;
                animControl.isCancelled = true;
                
                if (animationFrameId) {
                    cancelAnimationFrame(animationFrameId);
                    animationFrameId = null;
                }

                // 记录动画开始时的父容器
                const startParent = pieces[0]?.container.parent;

                // 强制设置到最终状态
                animations.forEach(anim => {
                    anim.piece.isRotating = false;
                    anim.piece.container.rotation = anim.targetRot * Math.PI / 180;
                    
                    // 防御性修复：只有当父容器没有发生变化时，才强制设置计算出的 x,y
                    // 如果父容器变了（例如从 puzzleBoard 移到了 dragGroupContainer），
                    // 则说明坐标系已变，不能再使用旧坐标系下计算出的 targetX/Y
                    if (anim.piece.container.parent === startParent) {
                        anim.piece.container.x = anim.targetX;
                        anim.piece.container.y = anim.targetY;
                    }
                    
                    this.events.emit(GameEvents.PIECE_ROTATE, anim.piece);
                });
                this.activeAnimations.delete(animControl);
            }
        };
        this.activeAnimations.add(animControl);

        const animate = () => {
            // 安全检查：如果动画已被取消或强制完成，不再执行任何更新
            if (animControl.isCancelled) return;

            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / duration, 1);
            
            // 缓动
            const eased = 1 - Math.pow(1 - progress, 3);

            // 当前这一帧，整体旋转了多少度（弧度）
            const currentAngleDelta = (degrees * eased) * (Math.PI / 180);
            const cos = Math.cos(currentAngleDelta);
            const sin = Math.sin(currentAngleDelta);

            animations.forEach(anim => {
                const { piece, startRot, deltaRot, relX, relY } = anim;

                // 1. 插值旋转角度 (自身旋转)
                const currentRot = startRot + deltaRot * eased;
                piece.container.rotation = currentRot * Math.PI / 180;

                // 2. 计算位置 (绕组中心旋转)
                // x' = x * cos - y * sin
                // y' = x * sin + y * cos
                const rotatedRelX = relX * cos - relY * sin;
                const rotatedRelY = relX * sin + relY * cos;

                piece.container.x = gCx + rotatedRelX;
                piece.container.y = gCy + rotatedRelY;
            });

            if (progress < 1) {
                animationFrameId = requestAnimationFrame(animate);
            } else {
                // 正常结束
                animControl.finish();
            }
        };

        animate();
    }

    /**
     * 键盘按下
     * @param {KeyboardEvent} event 键盘事件
     */
    onKeyDown(event) {
        if (!this.enabled || !this.rotationEnabled) return;

        // R键旋转当前拖拽的拼图块
        if (event.key === 'r' || event.key === 'R') {
            if (this.dragTarget) {
                const direction = event.shiftKey ? -90 : 90;
                this.rotatePiece(this.dragTarget, direction);
            }
        }
    }

    /**
     * 旋转拼图块
     * @param {Piece} piece 拼图块
     * @param {number} degrees 旋转角度
     */
    rotatePiece(piece, degrees) {
        if (piece.isRotating) return;

        // 修改：旋转前解除吸附
        if (piece.isSnapped) {
            piece.releaseSnap();
        }

        const svgSize = piece.size + piece.padding * 2;
        const centerOffset = svgSize / 2;

        // 如果还没有设置过pivot，先设置
        if (piece.container.pivot.x === 0 && piece.container.pivot.y === 0) {
            piece.container.pivot.set(centerOffset, centerOffset);
            piece.container.x += centerOffset;
            piece.container.y += centerOffset;
        }

        // 计算新的旋转角度
        const startRotation = piece.rotation;
        piece.rotation = (piece.rotation + degrees) % 360;
        if (piece.rotation < 0) piece.rotation += 360;

        // 动画旋转
        this.animateRotation(piece, startRotation, piece.rotation, 150);

        this.events.emit(GameEvents.PIECE_ROTATE, piece);
    }

    /**
     * 旋转动画
     * @param {Piece} piece 拼图块
     * @param {number} fromDegrees 起始角度
     * @param {number} toDegrees 目标角度
     * @param {number} duration 动画时长(ms)
     */
    animateRotation(piece, fromDegrees, toDegrees, duration) {
        piece.isRotating = true;
        const startTime = Date.now();
        let animationFrameId = null;

        // 处理跨越0度的情况
        let delta = toDegrees - fromDegrees;
        if (delta > 180) delta -= 360;
        if (delta < -180) delta += 360;

        // 记录动画开始时的父容器
        const startParent = piece.container.parent;

        // 动画控制对象
        const animControl = {
            isCancelled: false,
            piece: piece, // 记录关联的碎片，用于判定
            finish: () => {
                if (animControl.isCancelled) return;
                animControl.isCancelled = true;
                
                if (animationFrameId) {
                    cancelAnimationFrame(animationFrameId);
                    animationFrameId = null;
                }

                // 强制设置到最终状态
                piece.container.rotation = toDegrees * Math.PI / 180;
                piece.isRotating = false;

                // 防御性：如果父容器没变，才更新坐标（animateRotation 虽然不改坐标，但为了严谨性增加判定）
                // 实际上 animateRotation 只改角度，这里主要为了保持逻辑一致
                
                this.activeAnimations.delete(animControl);
            }
        };
        this.activeAnimations.add(animControl);

        const animate = () => {
            if (animControl.isCancelled) return;

            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / duration, 1);

            // 缓动函数 (ease-out)
            const eased = 1 - Math.pow(1 - progress, 3);

            const currentRotation = fromDegrees + delta * eased;
            piece.container.rotation = currentRotation * Math.PI / 180;

            if (progress < 1) {
                animationFrameId = requestAnimationFrame(animate);
            } else {
                animControl.finish();
            }
        };

        animate();
    }

    /**
     * 查找指定位置的拼图块
     * @param {number} x X坐标
     * @param {number} y Y坐标
     * @returns {Piece|null} 拼图块
     */
    findPieceAtPosition(x, y) {
        // 收集所有碎片及其在渲染树中的层级
        const pieces = this.puzzleBoard.pieces;
        const candidates = [];

        for (let i = 0; i < pieces.length; i++) {
            const piece = pieces[i];
            const bounds = piece.container.getBounds();
            if (x >= bounds.x && x <= bounds.x + bounds.width &&
                y >= bounds.y && y <= bounds.y + bounds.height) {
                // 用 container 在其 parent.children 中的索引作为层级依据
                const parent = piece.container.parent;
                const childIndex = parent ? parent.children.indexOf(piece.container) : 0;
                candidates.push({ piece, childIndex, parent });
            }
        }

        if (candidates.length === 0) return null;

        // 按渲染层级排序：parent.children 索引越大越在上层
        // 同一 parent 时比较 childIndex，不同 parent 时 stage 直属子节点优先于 puzzleBoard.container
        candidates.sort((a, b) => {
            if (a.parent === b.parent) {
                return b.childIndex - a.childIndex;
            }
            // 不同 parent，stage 上的拖拽容器比 puzzleBoard.container 层级更高
            const stageA = a.parent === this.app.stage ? 1 : 0;
            const stageB = b.parent === this.app.stage ? 1 : 0;
            return stageB - stageA;
        });

        return candidates[0].piece;
    }

    /**
     * 启用拖拽
     */
    enable() {
        this.enabled = true;
    }

    /**
     * 禁用拖拽
     */
    disable() {
        this.enabled = false;
        if (this.isDragging) {
            this.isDragging = false;
            this.dragTarget = null;
        }
    }

    /**
     * 启用/禁用旋转
     * @param {boolean} enabled 是否启用
     */
    setRotationEnabled(enabled) {
        this.rotationEnabled = enabled;
    }

    /**
     * 销毁
     */
    destroy() {
        this.app.stage.off('pointermove', this.onPointerMove);
        this.app.stage.off('pointerup', this.onPointerUp);
        this.app.stage.off('pointerupoutside', this.onPointerUp);
        document.removeEventListener('keydown', this.onKeyDown);
    }
}

export default DragController;
